/**
 * Startprosedyre-motor (RRS 26).
 *
 * Gitt millisekunder til start, returner gjeldende fase, hvilke flagg som
 * skal vises, og formatert nedtelling.
 */

export type Phase =
  | 'far_before'    // > 5 min
  | 'warning'       // 5 min → 4 min (Klasseflagg G opp)
  | 'preparatory'   // 4 min → 1 min (Klasseflagg + Blue Peter)
  | 'one_minute'    // 1 min → 10 sek (Klasseflagg, Blue Peter ned)
  | 'last_10s'      // 10 sek → 0
  | 'started'       // 0 → ∞ (fortsatt aktiv)
  | 'finished'      // Regattaen markert som ferdig av admin
  | 'cancelled';    // Regattaen avlyst

export interface ProcedureState {
  phase: Phase;
  msToStart: number;           // Kan være negativ (etter start)
  classFlagUp: boolean;        // G
  bluePeterUp: boolean;        // P
  apFlagUp: boolean;           // AP — hvis regattaen er utsatt
  countdownText: string;       // "02:34", "+01:23" eller "FERDIG"
  isBeforeStart: boolean;
  isFrozen: boolean;           // True = ikke oppdater opp/ned lenger
}

export type RaceRunStatus =
  | 'scheduled'
  | 'postponed'
  | 'started'
  | 'finished'
  | 'cancelled';

export function computeProcedure(
  msToStart: number,
  status: RaceRunStatus,
): ProcedureState {
  if (status === 'finished') {
    return {
      phase: 'finished',
      msToStart,
      classFlagUp: false,
      bluePeterUp: false,
      apFlagUp: false,
      countdownText: 'FERDIG',
      isBeforeStart: false,
      isFrozen: true,
    };
  }

  if (status === 'cancelled') {
    return {
      phase: 'cancelled',
      msToStart,
      classFlagUp: false,
      bluePeterUp: false,
      apFlagUp: false,
      countdownText: 'AVLYST',
      isBeforeStart: false,
      isFrozen: true,
    };
  }

  if (status === 'postponed') {
    return {
      phase: 'far_before',
      msToStart,
      classFlagUp: false,
      bluePeterUp: false,
      apFlagUp: true,
      countdownText: 'UTSATT',
      isBeforeStart: true,
      isFrozen: true,
    };
  }

  const isBeforeStart = msToStart > 0;

  let phase: Phase;
  let classFlagUp = false;
  let bluePeterUp = false;

  if (msToStart > 5 * 60_000) {
    phase = 'far_before';
  } else if (msToStart > 4 * 60_000) {
    phase = 'warning';
    classFlagUp = true;
  } else if (msToStart > 1 * 60_000) {
    phase = 'preparatory';
    classFlagUp = true;
    bluePeterUp = true;
  } else if (msToStart > 10_000) {
    phase = 'one_minute';
    classFlagUp = true;
  } else if (msToStart > 0) {
    phase = 'last_10s';
    classFlagUp = true;
  } else {
    phase = 'started';
  }

  return {
    phase,
    msToStart,
    classFlagUp,
    bluePeterUp,
    apFlagUp: false,
    countdownText: formatCountdown(msToStart),
    isBeforeStart,
    isFrozen: false,
  };
}

function formatCountdown(ms: number): string {
  if (ms > 0) {
    const totalSec = Math.ceil(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }
  const elapsed = Math.floor(-ms / 1000);
  const min = Math.floor(elapsed / 60);
  const sec = elapsed % 60;
  return `+${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/**
 * Gitt forrige og nåværende msToStart, returner hvilke lydsignaler som skal spilles.
 *
 * Grensepunktene er: −5:00, −4:00, −3:00, −2:00, −1:00, −0:10..−0:01, 0:00
 *
 * En grensepunkt trigges akkurat når vi krysser det (fra "før" til "på eller etter").
 * Bruk `floorSeconds()` for å sammenligne hele sekunder.
 */
export interface AudioTrigger {
  kind: 'short' | 'long';
  reason: string;
}

export function detectAudioTriggers(
  prevMs: number,
  currentMs: number,
  status: RaceRunStatus,
): AudioTrigger[] {
  if (status !== 'scheduled' && status !== 'started') return [];
  if (prevMs === currentMs) return [];

  const triggers: AudioTrigger[] = [];
  const prevSec = Math.ceil(prevMs / 1000);
  const currSec = Math.ceil(currentMs / 1000);

  // Nedtelling beveger seg fra høyere til lavere sekunder når vi går mot start.
  if (currSec >= prevSec) return []; // Ikke tid å fremad (kan skje ved sync-justering)

  // Sjekk alle hele sekunder mellom prevSec og currSec
  for (let s = prevSec - 1; s >= currSec; s--) {
    // Minutt-markering: 300, 240, 180, 120, 60 (og 0)
    if (s === 300) triggers.push({ kind: 'short', reason: 'Varselssignal (−5 min)' });
    else if (s === 240) triggers.push({ kind: 'short', reason: 'Klarsignal (−4 min)' });
    else if (s === 180) triggers.push({ kind: 'short', reason: '−3 min' });
    else if (s === 120) triggers.push({ kind: 'short', reason: '−2 min' });
    else if (s === 60) triggers.push({ kind: 'long', reason: 'Ett-minutts-signal' });
    // 10-sekunders-markeringer fra 50 til 20 sek igjen
    else if (s === 50 || s === 40 || s === 30 || s === 20) {
      triggers.push({ kind: 'short', reason: `−${s} sek` });
    }
    else if (s >= 1 && s <= 10) triggers.push({ kind: 'short', reason: `−${s} sek` });
    else if (s === 0) triggers.push({ kind: 'long', reason: 'Start!' });
  }

  return triggers;
}
